import { useTranslation } from 'react-i18next';
import { ConfigProvider } from 'antd';
import {
  ButtonGroupContainer,
  Footer,
  ModalHeader,
  ModalCloseButton,
  StyledModalMessage,
} from '../MachineDetailsRobotLiveMapScreen.styles';
import { CleaningCommandErrorAlert } from '../../CleaningCommandErrorAlert/CleaningCommandErrorAlert';
import { CANCEL_CLEANING_COMMAND_TYPES, SEND_HOME_COMMAND_TYPES } from '../../../utils/constants';
import { SEND_HOME_MODAL, CANCEL_CLEANING_MODAL, SKIP_ROUTE_MODAL } from './ModalMessage.constants';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import { BRangeCleaningCommandType } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { Styling } from 'app/cross-cutting-concerns/styling/Styling';
import { DependencyInjection } from 'app/cross-cutting-concerns/dependency-injection/DependencyInjection';
import { KCCThemeGlobalStyles } from 'config/theme/kccTheme/globalStyles';

export enum ModalType {
  SEND_HOME = 'SEND_HOME',
  CANCEL_CLEANING = 'CANCEL_CLEANING',
  SKIP_ROUTE = 'SKIP_ROUTE',
}

interface IModalMessageProps {
  isOpen: boolean;
  /** True while this modal's command is in flight. */
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Cancel-cleaning modal only: its secondary button sends the robot home instead of dismissing. */
  onSecondary?: () => void;
  type: ModalType;
}

interface IModalConfig {
  data: { title: string; modalMessage: string };
  /** Commands whose failure is shown in the modal footer; none for skip route yet. */
  commandTypes: BRangeCleaningCommandType[];
  /** True when the secondary button sends the robot home instead of dismissing. */
  secondaryIsSendHome: boolean;
}

// TODO: add skip-route commandTypes once the skip command has an error state.
const MODAL_CONFIG_BY_TYPE: Record<ModalType, IModalConfig> = {
  [ModalType.SEND_HOME]: { data: SEND_HOME_MODAL, commandTypes: SEND_HOME_COMMAND_TYPES, secondaryIsSendHome: false },
  [ModalType.CANCEL_CLEANING]: {
    data: CANCEL_CLEANING_MODAL,
    commandTypes: CANCEL_CLEANING_COMMAND_TYPES,
    secondaryIsSendHome: true,
  },
  [ModalType.SKIP_ROUTE]: { data: SKIP_ROUTE_MODAL, commandTypes: [], secondaryIsSendHome: false },
};

export const ModalMessage = ({
  isOpen,
  isPending,
  onClose,
  onConfirm,
  onSecondary,
  type,
}: IModalMessageProps): JSX.Element => {
  const { t } = useTranslation();
  const styling = DependencyInjection.resolve<Styling>(Styling);
  const { data: modalData, commandTypes, secondaryIsSendHome } = MODAL_CONFIG_BY_TYPE[type];

  return (
    <ConfigProvider theme={styling.getAntDesignKCCThemeConfig()} modal={{ className: 'kcc-theme-modal' }}>
      <KCCThemeGlobalStyles />
      <StyledModalMessage
        open={isOpen}
        onCancel={onClose}
        centered
        destroyOnClose
        closable={false}
        keyboard={false}
        maskClosable={!isPending}
        title={
          <ModalHeader>
            {t(modalData.title).concat('?')}
            <ModalCloseButton aria-label={t('common.close')} disabled={isPending} onClick={onClose} />
          </ModalHeader>
        }
        footer={
          <Footer>
            {commandTypes.length > 0 && <CleaningCommandErrorAlert commandTypes={commandTypes} isClosable={false} />}

            <ButtonGroupContainer style={{ justifyContent: 'flex-end' }}>
              <KccButton type="secondary" disabled={isPending} onClick={secondaryIsSendHome ? onSecondary : onClose}>
                {secondaryIsSendHome ? t(SEND_HOME_MODAL.title) : t('common.cancel')}
              </KccButton>
              <KccButton
                type="primary"
                disabled={isPending}
                icon={isPending ? <PendingSpinnerIcon /> : undefined}
                onClick={onConfirm}
              >
                {t(modalData.title)}
              </KccButton>
            </ButtonGroupContainer>
          </Footer>
        }
      >
        {t(modalData.modalMessage)}
      </StyledModalMessage>
    </ConfigProvider>
  );
};
